import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useDrag } from 'react-dnd';
import { Icons } from '../Icons';
import { DisplaySetMessageListTooltip } from '../DisplaySetMessageListTooltip';
import { TooltipTrigger, TooltipContent, TooltipProvider, Tooltip } from '../Tooltip';
import { Button } from '../Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../DropdownMenu';
import { ThumbnailImagePlaceholder } from './ThumbnailImagePlaceholder';

/**
 * Display a thumbnail for a display set.
 */
const Thumbnail = ({
  displaySetInstanceUID,
  className,
  imageSrc,
  imageAltText,
  description,
  seriesNumber,
  numInstances,
  loadingProgress,
  countIcon,
  messages,
  dragData = {},
  isActive,
  onClick,
  onDoubleClick,
  viewPreset = 'thumbnails',
  modality,
  isHydratedForDerivedDisplaySet = false,
  canReject = false,
  onReject = () => { },
  isTracked = false,
  thumbnailType = 'thumbnail',
  onClickUntrack = () => { },
  onThumbnailContextMenu,
  cellWidth,
}: withAppTypes): React.ReactNode => {
  // TODO: We should wrap our thumbnail to create a "DraggableThumbnail", as
  // this will still allow for "drag", even if there is no drop target for the
  // specified item.
  const [collectedProps, drag, dragPreview] = useDrag({
    type: 'displayset',
    item: { ...dragData },
    canDrag: function (monitor) {
      return Object.keys(dragData).length !== 0;
    },
  });

  const [lastTap, setLastTap] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 1200px)').matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1200px)');
    const handler = (e) => setIsSmallScreen(e.matches);
    
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  useEffect(() => {
    setImageLoaded(false);
  }, [imageSrc]);

  // Some images may already be cached when this component mounts. In that case
  // `onLoad` may not fire, so we check `complete`/`naturalWidth` to remove
  // the placeholder immediately.
  useEffect(() => {
    if (!imageSrc) {
      return;
    }

    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [imageSrc]);

  const handleTouchEnd = e => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    if (tapLength < 300 && tapLength > 0) {
      onDoubleClick(e);
    } else {
      onClick(e);
    }
    setLastTap(currentTime);
  };

  const renderThumbnailPreset = () => {
    return (
      <div className={`flex w-full flex-col items-center justify-center ${isSmallScreen ? 'gap-[1px] p-[3px]' : 'gap-[2px] p-[4px]'}`}>
        {/* Image area — fills all available width, locks to 1:1 aspect ratio */}
        <div className="relative w-full" style={{ aspectRatio: '1 / 1' }}>
          {(!imageSrc || !imageLoaded) && <ThumbnailImagePlaceholder className="z-[1] absolute inset-0" />}
          {imageSrc ? (
            <img
              ref={imgRef}
              src={imageSrc}
              alt={imageAltText || ''}
              className={classnames(
                'absolute inset-0 z-[1] h-full w-full rounded object-contain transition-opacity duration-200',
                imageLoaded ? 'opacity-100' : 'opacity-0'
              )}
              crossOrigin="anonymous"
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(true)}
            />
          ) : null}

          {/* bottom left */}
          <div className={`bg-muted absolute bottom-0 left-0 z-[2] flex items-center gap-[2px] ${isSmallScreen ? 'h-[10px] p-[2px]' : 'h-[14px] p-[4px]'}`}>
            <div
              className={classnames(
                'rounded-[2px]',
                isSmallScreen ? 'h-[5px] w-[5px]' : 'h-[10px] w-[10px]',
                isActive || isHydratedForDerivedDisplaySet ? 'bg-highlight' : 'bg-primary/65',
                loadingProgress && loadingProgress < 1 && 'bg-primary/25'
              )}
            ></div>
            <div className={`text-white ${isSmallScreen ? 'text-[7px]' : 'text-[11px]'}`}>{modality}</div>
          </div>

          {/* top right */}
          <div className="absolute top-0 right-0 z-[2] flex items-center">
            <DisplaySetMessageListTooltip
              messages={messages}
              id={`display-set-tooltip-${displaySetInstanceUID}`}
            />
            {canReject && (
              <Icons.Trash
                className={`text-red-500 ${isSmallScreen ? 'h-[12px] w-[12px]' : 'h-[20px] w-[20px]'}`}
                onClick={onReject}
              />
            )}
            {isTracked && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <div className="group">
                      <Icons.StatusTracking className={`text-primary-light group-hover:hidden ${isSmallScreen ? 'h-[12px] w-[12px]' : 'h-[20px] w-[20px]'}`} />
                      <Icons.Cancel
                        className={`text-primary-light hidden group-hover:block ${isSmallScreen ? 'h-[10px] w-[10px]' : 'h-[15px] w-[15px]'}`}
                        onClick={onClickUntrack}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <div className="flex flex-1 flex-row">
                      <div className="flex-2 flex items-center justify-center pr-4">
                        <Icons.InfoLink className="text-primary-active" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <span>
                          <span className="text-white">
                            {isTracked ? 'Series is tracked' : 'Series is untracked'}
                          </span>
                        </span>
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {/* bottom right */}
          <div className={`absolute bottom-0 right-0 z-[2] flex items-center ${isSmallScreen ? 'p-[0px]' : ''}`}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  className={`text-lightblue-main hidden group-hover:inline-flex data-[state=open]:inline-flex ${isSmallScreen ? 'h-[12px] w-[12px]' : ''}`}
                >
                  <Icons.More className={isSmallScreen ? 'h-[10px] w-[10px]' : ''} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                asChild
                hideWhenDetached
              >
                <DropdownMenuItem
                  onSelect={() => {
                    onThumbnailContextMenu('openDICOMTagViewer', {
                      displaySetInstanceUID,
                    });
                  }}
                  className="bg-grey-main gap-[6px]"
                >
                  <Icons.DicomTagBrowser />
                  Tag Browser
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Description area — full width, fixed text height */}
        <div
          className={`flex w-full flex-col ${isSmallScreen ? 'gap-[4px] pt-[1px]' : 'gap-[6px] pt-[2px]'}`}
        >
          <div
            data-cy="thumbnail-series-description"
            className={`w-full overflow-hidden text-ellipsis whitespace-nowrap text-white ${isSmallScreen ? 'text-[8px] h-[9px]' : 'text-[12px] h-[13px]'}`}
          >
            {description}
          </div>
          <div
            className={`flex items-center overflow-hidden text-[8px] ${isSmallScreen ? 'h-[9px] gap-[6px]' : 'h-[12px] gap-[10px]'}`}
          >
            <div className={`text-muted-foreground ${isSmallScreen ? 'text-[7px]' : 'text-[12px]'}`}> S:{seriesNumber}</div>
            <div className={`text-muted-foreground ${isSmallScreen ? 'text-[7px]' : 'text-[12px]'}`}>
              <div className="flex items-center gap-[4px]">
                {' '}
                {countIcon ? (
                  React.createElement(Icons[countIcon] || Icons.MissingIcon, { className: isSmallScreen ? 'w-2' : 'w-3' })
                ) : (
                  <Icons.InfoSeries className={isSmallScreen ? 'w-2' : 'w-3'} />
                )}
                <div>{numInstances}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderListPreset = () => {
    return (
      <div className="flex h-full w-full items-center justify-between pr-[8px] pl-[8px] pt-[4px] pb-[4px]">
        <div className="relative flex h-[32px] items-center gap-[8px]">
          <div
            className={classnames(
              'h-[32px] w-[4px] rounded-[2px]',
              isActive || isHydratedForDerivedDisplaySet ? 'bg-highlight' : 'bg-primary/65',
              loadingProgress && loadingProgress < 1 && 'bg-primary/25'
            )}
          ></div>
          <div className="flex h-full flex-col">
            <div className="flex items-center gap-[7px]">
              <div className="text-[13px] text-white">{modality}</div>

              <div className="max-w-[160px] overflow-hidden overflow-ellipsis whitespace-nowrap text-[13px] text-white">
                {description}
              </div>
            </div>

            <div className="flex h-[12px] items-center gap-[7px] overflow-hidden">
              <div className="text-muted-foreground text-[12px]"> S:{seriesNumber}</div>
              <div className="text-muted-foreground text-[12px]">
                <div className="flex items-center gap-[4px]">
                  {' '}
                  {countIcon ? (
                    React.createElement(Icons[countIcon] || Icons.MissingIcon, { className: 'w-3' })
                  ) : (
                    <Icons.InfoSeries className="w-3" />
                  )}
                  <div>{numInstances}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex h-full items-center gap-[4px]">
          <DisplaySetMessageListTooltip
            messages={messages}
            id={`display-set-tooltip-${displaySetInstanceUID}`}
          />
          {canReject && (
            <Icons.Trash
              className="h-[20px] w-[20px] text-red-500"
              onClick={onReject}
            />
          )}
          {isTracked && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <div className="group">
                    <Icons.StatusTracking className="text-primary-light h-[20px] w-[20px] group-hover:hidden" />
                    <Icons.Cancel
                      className="text-primary-light hidden h-[15px] w-[15px] group-hover:block"
                      onClick={onClickUntrack}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <div className="flex flex-1 flex-row">
                    <div className="flex-2 flex items-center justify-center pr-4">
                      <Icons.InfoLink className="text-primary-active" />
                    </div>
                    <div className="flex flex-1 flex-col">
                      <span>
                        <span className="text-white">
                          {isTracked ? 'Series is tracked' : 'Series is untracked'}
                        </span>
                      </span>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                className="text-lightblue-main hidden group-hover:inline-flex data-[state=open]:inline-flex"
              >
                <Icons.More />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              asChild
              hideWhenDetached
            >
              <DropdownMenuItem
                onSelect={() => {
                  onThumbnailContextMenu('openDICOMTagViewer', {
                    displaySetInstanceUID,
                  });
                }}
                className="bg-grey-main gap-[6px]"
              >
                <Icons.DicomTagBrowser />
                Tag Browser
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  };

  const thumbnailCellStyle =
    viewPreset === 'thumbnails' && cellWidth
      ? { width: cellWidth, maxWidth: cellWidth, flexShrink: 0 }
      : undefined;

  return (
    <div
      style={thumbnailCellStyle}
      className={classnames(
        className,
        'group flex h-auto cursor-pointer select-none flex-col bg-grey-dark outline-none hover:bg-grey-light',
        viewPreset === 'thumbnails' && !cellWidth && 'min-w-0 w-full',
        viewPreset === 'list' && 'col-span-full h-[40px] w-full max-w-[300px]'
      )}
      id={`thumbnail-${displaySetInstanceUID}`}
      data-cy={
        thumbnailType === 'thumbnailNoImage'
          ? 'study-browser-thumbnail-no-image'
          : 'study-browser-thumbnail'
      }
      data-series={seriesNumber}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onTouchEnd={handleTouchEnd}
      role="button"
    >
      <div
        ref={drag}
        className="w-full"
      >
        {viewPreset === 'thumbnails' && renderThumbnailPreset()}
        {viewPreset === 'list' && renderListPreset()}
      </div>
    </div>
  );
};

Thumbnail.propTypes = {
  displaySetInstanceUID: PropTypes.string.isRequired,
  className: PropTypes.string,
  imageSrc: PropTypes.string,
  /**
   * Data the thumbnail should expose to a receiving drop target. Use a matching
   * `dragData.type` to identify which targets can receive this draggable item.
   * If this is not set, drag-n-drop will be disabled for this thumbnail.
   *
   * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
   */
  dragData: PropTypes.shape({
    /** Must match the "type" a dropTarget expects */
    type: PropTypes.string.isRequired,
  }),
  imageAltText: PropTypes.string,
  description: PropTypes.string.isRequired,
  seriesNumber: PropTypes.any,
  numInstances: PropTypes.number.isRequired,
  loadingProgress: PropTypes.number,
  messages: PropTypes.object,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  viewPreset: PropTypes.string,
  modality: PropTypes.string,
  isHydratedForDerivedDisplaySet: PropTypes.bool,
  canReject: PropTypes.bool,
  onReject: PropTypes.func,
  isTracked: PropTypes.bool,
  onClickUntrack: PropTypes.func,
  countIcon: PropTypes.string,
  thumbnailType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage']),
  cellWidth: PropTypes.number,
};

export { Thumbnail };